import React, { useState, useEffect } from 'react';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import {
  errorMessage,
  onboardingStepId,
  pagination,
  responseTexts,
  speciesSubsteps,
} from '@helpers/Constants';
import { RequiredFlagType, SpeciesForm } from '@helpers/Models';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Assets } from '@helpers/Constants/Assets';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import {
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Pagination from '@components/Pagination';
import { isModified } from '@services/isModified';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';

export const Species: React.FC = () => {
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;
  const { updateOnboardingStep } = useLocalStorage();
  const [speciesData, setSpeciesData] = useState<SpeciesForm[]>([]);
  const navigate = useNavigate();
  const [speciesSelected, setSpeciesSelected] = useState(false);
  const { showToastMessage } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchKey, setSearchKey] = useState('');
  const [initialList, setInitialList] = useState<SpeciesForm[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /**
   * Fetches species data whenever the current page, number of items per page,
   * or search key changes.
   */
  useEffect(() => {
    fetchSpecies(currentPage, searchKey, itemsPerPage);
  }, [currentPage, itemsPerPage, searchKey]);

  /**
   * Fetches species data from the server.
   */
  const fetchSpecies = async (
    page: number,
    searchKey: string,
    count: number = itemsPerPage,
  ): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES, {
        page,
        count,
        searchKey,
      });
      setSpeciesData(response.data.data.species);
      setInitialList(response.data.data.species);
      setCurrentPage(response.data.data.currentPage || 1);
      setTotalPages(response.data.data.totalPages || 1);
      // Reset unsaved changes after fresh fetch
      setHasUnsavedChanges(false);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the selection state of all species.
   */
  const toggleSelectAll = () => {
    const newSpeciesData = speciesData.map((species) => ({
      ...species,
      availableFlag: !speciesSelected,
    }));
    setSpeciesData(newSpeciesData);
    setSpeciesSelected(!speciesSelected);
    // Recompute unsaved changes with the new data
    setHasUnsavedChanges(isModified(initialList, newSpeciesData));
  };

  /**
   * Handles toggling of species requirement flags.
   */
  const handleFlagChange = (speciesId: number, flagKey: RequiredFlagType) => {
    const updatedSpeciesData = speciesData.map((species) => {
      if (species.id !== speciesId) return species;

      if (flagKey === 'availableFlag') {
        const availableFlag = !species.availableFlag;
        return {
          ...species,
          availableFlag: availableFlag,
          requireLengthFlag: availableFlag ? species.requireLengthFlag : false,
          requireCitesFlag: availableFlag ? species.requireCitesFlag : false,
          requirePermitFlag: availableFlag ? species.requirePermitFlag : false,
          requireLicenseFlag: availableFlag ? species.requireLicenseFlag : false,
        };
      }

      const currentFlagValue = !species[flagKey];
      return {
        ...species,
        [flagKey]: currentFlagValue,
        availableFlag: currentFlagValue ? true : species.availableFlag,
      };
    });
    setSpeciesData(updatedSpeciesData);
    // Recompute unsaved changes with the new data
    setHasUnsavedChanges(isModified(initialList, updatedSpeciesData));
  };

  /**
   * Validates if there are any selected species across all pages.
   */
  const validateSpeciesSelectionAcrossAllPages = async (): Promise<boolean> => {
    const hasLocalSelections = speciesData.some(
      (species) => species.availableFlag === true,
    );
    if (hasLocalSelections) {
      return true;
    }
    try {
      const allSpeciesResponse = await Axios.post(ApiEndpoints.FETCH_SPECIES, {
        page: 1,
        count: 1000,
        searchKey: '',
      });
      return allSpeciesResponse.data.data.species.some(
        (species: SpeciesForm) => species.availableFlag === true,
      );
    } catch (error) {
      return hasLocalSelections;
    }
  };

  /**
   * Handles form submission.
   */
  const handleSubmit: (
    event: React.FormEvent,
    nextPage: boolean,
  ) => Promise<void> = async (event, nextPage) => {
    event.preventDefault();

    const hasSelectedSpecies = await validateSpeciesSelectionAcrossAllPages();
    if (!hasSelectedSpecies) {
      showToastMessage(
        errorMessage.SPECIES_SELECTION_VALIDATION_ERROR,
        'error',
      );
      return;
    }

    const isDataModified = isModified(initialList, speciesData);
    if (initialList && !isDataModified) {
      if (nextPage) {
        setHasUnsavedChanges(false);
        if (fromSettings) {
          navigate(FrontendRoutes.SERVICES, { state: { fromSettings } });
        } else {
          navigate(FrontendRoutes.SERVICES);
        }
      }
      return;
    }

    const payload = speciesData.map((species) => {
      if (species.butcherSpeciesId) {
        return {
          id: species.id,
          butcherSpeciesId: species.butcherSpeciesId || null,
          availableFlag: species.availableFlag || false,
          requireLengthFlag: species.requireLengthFlag || false,
          requirePermitFlag: species.requirePermitFlag || false,
          requireLicenseFlag: species.requireLicenseFlag || false,
          requireCitesFlag: species.requireCitesFlag || false,
        };
      } else {
        return {
          id: species.id,
          availableFlag: species.availableFlag || false,
          requireLengthFlag: species.requireLengthFlag || false,
          requirePermitFlag: species.requirePermitFlag || false,
          requireLicenseFlag: species.requireLicenseFlag || false,
          requireCitesFlag: species.requireCitesFlag || false,
        };
      }
    });

    try {
      const response = await Axios.post(ApiEndpoints.CREATE_SPECIES, {
        species: payload,
      });

      const forMasterSpecies = response.data.data.map(
        (speciesData: { butcherSpeciesId: number }) => ({
          ...speciesData,
          id: speciesData.butcherSpeciesId,
        }),
      );
      localStorage.setItem('speciesData', JSON.stringify(forMasterSpecies));

      // Determine if any species was previously selected
      const hadAnySelected = initialList.some(s => s.availableFlag === true);
      const successMsg = hadAnySelected
        ? responseTexts.SPECIES_UPDATE_SUCCESS || 'Species updated successfully.'
        : responseTexts.SPECIES_ADD_SUCCESS;

      showToastMessage(successMsg, 'success');
      fetchSpecies(currentPage, searchKey, itemsPerPage);
      updateOnboardingStep(onboardingStepId.SPECIES);
      setHasUnsavedChanges(false);

      if (nextPage) {
        if (fromSettings) {
          navigate(FrontendRoutes.SERVICES, { state: { fromSettings } });
        } else {
          navigate(FrontendRoutes.SERVICES);
        }
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  /**
   * Saves current changes if there are any modifications.
   */
  const saveChangesIfModified = async (): Promise<boolean> => {
    const isDataModified = isModified(initialList, speciesData);
    if (!isDataModified) {
      return true;
    }

    const hasSelectedSpecies = await validateSpeciesSelectionAcrossAllPages();
    if (!hasSelectedSpecies) {
      showToastMessage(
        errorMessage.SPECIES_SELECTION_VALIDATION_ERROR,
        'error',
      );
      return false;
    }

    const payload = speciesData.map((species) => {
      if (species.butcherSpeciesId) {
        return {
          id: species.id,
          butcherSpeciesId: species.butcherSpeciesId || null,
          availableFlag: species.availableFlag || false,
          requireLengthFlag: species.requireLengthFlag || false,
          requirePermitFlag: species.requirePermitFlag || false,
          requireLicenseFlag: species.requireLicenseFlag || false,
          requireCitesFlag: species.requireCitesFlag || false,
        };
      } else {
        return {
          id: species.id,
          availableFlag: species.availableFlag || false,
          requireLengthFlag: species.requireLengthFlag || false,
          requirePermitFlag: species.requirePermitFlag || false,
          requireLicenseFlag: species.requireLicenseFlag || false,
          requireCitesFlag: species.requireCitesFlag || false,
        };
      }
    });

    try {
      await Axios.post(ApiEndpoints.CREATE_SPECIES, {
        species: payload,
      });
      showToastMessage(responseTexts.CHANGES_SAVE_SUCCESS, 'success');
      setHasUnsavedChanges(false);
      return true;
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
      return false;
    }
  };

  /**
   * Handles page change in pagination.
   */
  const handlePageChange = async (page: number) => {
    const saveSuccessful = await saveChangesIfModified();
    if (saveSuccessful) {
      setCurrentPage(page);
    }
  };

  /**
   * Handles items per page change.
   */
  const handleItemsPerPageChange = async (newItemsPerPage: number) => {
    const saveSuccessful = await saveChangesIfModified();
    if (saveSuccessful) {
      setCurrentPage(1);
      setItemsPerPage(newItemsPerPage);
    }
  };

  /**
   * Handles search input changes.
   */
  const handleSearchChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const newSearchKey = e.target.value;
    const saveSuccessful = await saveChangesIfModified();
    if (saveSuccessful) {
      setSearchKey(newSearchKey);
    }
  };

  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Species"
          activeStep={1}
          subNavSteps={speciesSubsteps}
          fromSettings={fromSettings ? true : false}
          forStoreSetting={false}
        />
      </div>
      <div className="shadow-lg mt-3">
        <div className="flex justify-between items-center mb-6">
          <h1 className="font-semibold text-xl px-5 mt-3">Species</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={handleSearchChange}
                value={searchKey}
                className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="overflow-y-auto h-[calc(100vh-500px)]">
            <table className="w-full table-auto">
              <thead className="bg-gray-100 sticky top-0 z-20 shadow">
                <tr className="border-b">
                  <th className="px-4 py-2 text-center font-semibold whitespace-nowrap">
                    Available{' '}
                    <input
                      onChange={toggleSelectAll}
                      type="checkbox"
                      className="ml-2 form-checkbox h-4 w-4 accent-primary focus:ring-primary text-primary rounded-full"
                    />
                  </th>
                  <th className="px-4 py-2">
                    <div className="flex items-center gap-3 font-semibold">
                      <span>Species</span>
                      <img
                        src={Assets.SORT_ICON}
                        alt="Sort"
                        className="h-3 w-3"
                      />
                    </div>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold">
                    Require Length
                  </th>
                  <th className="px-4 py-2 text-center font-semibold">
                    Require License
                  </th>
                  <th className="px-4 py-2 text-center font-semibold">
                    Require Permit
                  </th>
                  <th className="px-4 py-2 text-center font-semibold">
                    Require CITES no.
                  </th>
                </tr>
              </thead>
              <tbody>
                {speciesData.length > 0 ? (
                  speciesData.map((species) => (
                    <tr
                      key={species.id}
                      className={`${species.availableFlag
                        ? 'bg-tertiary border border-primary'
                        : 'bg-[#FFFEFA]'
                        }`}
                    >
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                          checked={species.availableFlag ?? false}
                          data-testid={`available-checkbox-${species.id}`}
                          onChange={() =>
                            handleFlagChange(species.id, 'availableFlag')
                          }
                        />
                      </td>
                      <td className="px-4 py-2">{species.name}</td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          aria-label="Required Length"
                          data-testid={`length-checkbox-${species.id}`}
                          className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                          checked={species.requireLengthFlag ?? false}
                          onChange={() =>
                            handleFlagChange(species.id, 'requireLengthFlag')
                          }
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          aria-label="Required License"
                          data-testid={`license-checkbox-${species.id}`}
                          className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                          checked={species.requireLicenseFlag ?? false}
                          onChange={() =>
                            handleFlagChange(species.id, 'requireLicenseFlag')
                          }
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          aria-label="Required Permit"
                          data-testid={`permit-checkbox-${species.id}`}
                          className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                          checked={species.requirePermitFlag ?? false}
                          onChange={() =>
                            handleFlagChange(species.id, 'requirePermitFlag')
                          }
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                          checked={species.requireCitesFlag ?? false}
                          data-testid={`cites-checkbox-${species.id}`}
                          onChange={() =>
                            handleFlagChange(species.id, 'requireCitesFlag')
                          }
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-2 text-center">
                      No species data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div id="footer-content" className="mt-auto">
        <div className="p-4 mt-4 shadow-sm">
          <div>
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
          <div className="flex w-full py-4 justify-end xl:justify-end lg:justify-end gap-4">
            {/* Save Button – disabled unless changes exist */}
            <button
              type="button"
              onClick={(e) => handleSubmit(e, false)}
              disabled={!hasUnsavedChanges}
              className={`lg:w-[130px] xl:w-[150px] h-10 px-6 py-1.5 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasUnsavedChanges
                  ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                  : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
                }`}
            >
              Save
            </button>
            <button
              type="submit"
              onClick={(e) => handleSubmit(e, true)}
              className="lg:w-[130px] xl:w-[150px] h-10 px-6 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
            >
              <span>Next</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} speciesSetup={true} />
      )}
    </>
  );
};